import {useRef, useState} from "react";
import styled from "styled-components";
import theme from "../../style/theme";
import {SqaureCheckBox} from "../checkbox/SquareCheckBox";
import ExpandLessIcon from "../icons/ExpandLessIcon/ExpandLessIcon";
import ExpandMoreIcon from "../icons/ExpandMoreIcon/ExpandMoreIcon";
import {useClickOutside} from "../../utils/click/handle-click";

interface Props {
    defaultKey: string;
    onClickFilterMultiSelectItem: (key: string) => void;
    keys: string[];
    candidateMap: { [key: string]: string };
    width?: string;
    placeholder?: string;
}

export const Selector: React.FC<Props> = (
    {
        defaultKey,
        keys,
        onClickFilterMultiSelectItem,
        candidateMap,
        width,
        placeholder,
    }
) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const handleClickBox = () => {
        setOpen(!open);
    };

    useClickOutside(ref, () => setOpen(false));

    return (
        <div ref={ref}>
            <SelectedItem keys={keys} width={width} onClick={handleClickBox}>
                {!keys.length && placeholder ? (
                    <PlaceholderItemLabel>{placeholder}</PlaceholderItemLabel>
                ) : (
                    <SelectedItemLabel keys={keys}>
                        {keys.map((key) => candidateMap[key]).join(",") || defaultKey}
                    </SelectedItemLabel>
                )}
                <RightSelectedItemIconWrapper>
                    {open ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                </RightSelectedItemIconWrapper>
            </SelectedItem>
            {open && (
                <MenuItemList width={width}>
                    {Object.keys(candidateMap).map((candidate) => {
                        const isSelected = keys.includes(candidate);
                        return (
                            <MenuItem
                                key={candidate}
                                selected={isSelected}
                                onClick={() => onClickFilterMultiSelectItem(candidate)}
                            >
                                <LeftMenuItemIconWrapper>
                                    <SqaureCheckBox
                                        checked={isSelected}
                                        fill={theme.palette.primitives.yellow400}
                                    />
                                </LeftMenuItemIconWrapper>
                                <MenuItemLabel>{candidateMap[candidate]}</MenuItemLabel>
                            </MenuItem>
                        );
                    })}
                </MenuItemList>
            )}
        </div>
    );
};

interface SelectedItemProps {
    keys: string[];
    width?: string;
}

const SelectedItem = styled.div<SelectedItemProps>`
  width: ${(props) => props.width || "14.25rem"};
  height: 2.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  border: ${`1px solid ${theme.palette.primitives.gray300}`};
  background-color: ${theme.palette.primitives.white};

  &:hover {
    background-color: #f5f6f9;
  }
`;

interface SelectedItemLabelProps {
    keys: string[];
}

const SelectedItemLabel = styled.div<SelectedItemLabelProps>`
  height: 2.625rem;
  margin-left: 0.75rem;
  font-style: normal;
  font-weight: 500;
  font-size: 0.938rem;
  color: ${theme.palette.primitives.gray700};
  display: flex;
  align-items: center;
`;

const PlaceholderItemLabel = styled.div`
  height: 2.625rem;
  margin-left: 0.75rem;
  color: ${theme.palette.primitives.gray300};
  display: flex;
  align-items: center;
  font-style: normal;
  font-size: 15px;
  line-height: 18px;
  font-weight: 300;
`;

const RightSelectedItemIconWrapper = styled.div`
  width: 36px;
  height: 24px;
  box-sizing: border-box;
  padding: 0 12px 0 7px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface MenuItemListProps {
    width?: string;
}

const MenuItemList = styled.div<MenuItemListProps>`
  width: ${(props) => props.width || "14.25rem"};
  max-height: 12.188rem;
  padding: 6.5px 0;
  position: absolute;
  background-color: #ffffff;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0px 6px 8px rgba(0, 0, 0, 0.2);
  border: solid 1px #bec3cb;
  z-index: 500;
  // max-height: calc(100vh - 400px);
  overflow-y: scroll;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 5px;
    background-color: #ffffff;
  }

  /* 스크롤바 색상 */

  &::-webkit-scrollbar-thumb {
    background-color: ${theme.palette.primitives.gray200};
    border-radius: 10px;
  }

  /* 스크롤바가 마우스 오버될 때 색상 */

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${theme.palette.primitives.gray300};
  }
`;

interface MenuItemProps {
    selected: boolean;
}

const MenuItem = styled.div<MenuItemProps>`
  height: 36px;
  display: flex;
  color: #3f444e;
  align-items: center;
  background-color: ${(props) =>
          props.selected ? theme.palette.primitives.yellow50 : "#ffffff"};

  &:hover {
    background-color: #f5f6f9;
  }

  font-size: 14px;
  font-weight: 500;
`;

const LeftMenuItemIconWrapper = styled.div`
  width: 42px;
  height: 36px;
  box-sizing: border-box;
  padding: 9px 8px 9px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MenuItemLabel = styled.div`
  height: 2.5rem;
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${theme.palette.primitives.black};
  display: flex;
  align-items: center;
`;
